<template>
  <div
    class="
      tw-grid
      tw-gap-y-4
      tw-gap-x-4
      tw-sticky
      tw-bottom-0
      tw-bg-white
      tw-py-6
      tw-border-solid
      tw-border-t
      tw-border-x-0
      tw-border-b-0
      tw-border-sf-neutral-lighter-2
      tw-z-10
    "
    :style="{
      gridColumn: outerContainerSpan,
      gridTemplateColumns: gridTemplate,
    }"
  >
    <div class="tw-pl-6" :style="{ gridColumn: errorContainerSpan }">
      <SfInputError
        v-show="errors.length > 0"
        class="tw-mt-1"
        :text="errors[0]"
      />
    </div>
    <div
      class="tw-inline-flex tw-items-center tw-justify-between tw-font-medium"
    >
      <span :class="iconClasses" data-testid="balanced-status-icon"></span>
      <span :class="textClasses" data-testid="total-debit">{{
        formatDebitDifference
      }}</span>
    </div>
    <div
      class="tw-text-right tw-font-medium"
      :class="textClasses"
      data-testid="total-credit"
    >
      {{ formatCreditDifference }}
    </div>
    <div class="tw-col-span2"></div>
  </div>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import useModalGridTemplate from '../composables/use-modal-grid-template'
import { baseColumns, emptyColumnControlRowSpan } from '../constants'
import { SfInputError } from '@silverfin/ui-components'

import { useAdjustmentsModalStore } from '@app/application/adjustments/store/adjustment-modal'

const modalStore = useAdjustmentsModalStore()

const {
  adjustmentErrors,
  columns: storedColumns,
  isBalanced,
  shouldDisplayErrors,
  totals,
} = storeToRefs(modalStore)

const { gridTemplate } = useModalGridTemplate()

const extraColumns = computed(() => Math.max(0, storedColumns.value.length - 7))
const errors = computed(() =>
  shouldDisplayErrors.value ? adjustmentErrors.value : []
)

const hasErrors = computed(() => errors.value.length > 0 || !isBalanced.value)

const outerContainerSpan = computed(
  () =>
    `span ${baseColumns + extraColumns.value} / span ${
      baseColumns + extraColumns.value
    }`
)

const errorContainerSpan = computed(
  () =>
    `span ${emptyColumnControlRowSpan + extraColumns.value} / span ${
      emptyColumnControlRowSpan + extraColumns.value
    }`
)

const iconClasses = computed(() => ({
  'sf-c-danger-light-1': hasErrors.value,
  'i-unreconciled': hasErrors.value,
  'sf-c-success-light-1': !hasErrors.value,
  'i-reconciled': !hasErrors.value,
}))

const textClasses = computed(() => ({
  'tw-text-sf-gray': !hasErrors.value,
  'tw-text-sf-danger-light-1': hasErrors.value,
}))

const formatCreditDifference = computed(() => {
  if (isBalanced.value) {
    return 0
  }

  if (totals.value[1] < totals.value[0]) {
    return new Intl.NumberFormat(window.Silverfin.currentUserLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totals.value[0] - totals.value[1])
  } else {
    return ''
  }
})

const formatDebitDifference = computed(() => {
  if (isBalanced.value) {
    return ''
  }

  if (totals.value[0] < totals.value[1]) {
    return new Intl.NumberFormat(window.Silverfin.currentUserLocale(), {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(totals.value[1] - totals.value[0])
  } else {
    return ''
  }
})
</script>
